import React, { useEffect, useRef, useState } from "react";
import {
  useTheme,
  CssBaseline,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { baseapi } from "../Environmentalurl";
import { useSelector } from "react-redux";
import {
  ApplicationType,
  date,
  entity_id,
  location,
  locationGeo,
  orderID,
  Platform,
} from "../Utils/Const";
import { useNavigate } from "react-router-dom";
import useIPAddress from "../Utils/useIPAddress";
import api from "../Utils/Api";

export default function CreditCard() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const md = useMediaQuery("(min-width:900px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  // ── Operator / dynamic field state ───────────────────────────────────────
  const [operator, setOperator] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [operators, setOperators] = useState([]);
  const [selectedOperatorData, setSelectedOperatorData] = useState(null);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [dynamicValues, setDynamicValues] = useState({});
  const [selectedField, setSelectedField] = useState("");

  // ── Bill fetch + pay state ───────────────────────────────────────────────
  const [billDetails, setBillDetails] = useState(null);
  const [billFetchOpen, setBillFetchOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [preparedPaymentPayload, setPreparedPaymentPayload] = useState(null);
  const [chargesLoading, setChargesLoading] = useState(false);
  // ── Charges summary dialog state ─────────────────────────────────────────
  const [chargesData, setChargesData] = useState(null); // holds GetCharges response
  const [chargesOpen, setChargesOpen] = useState(false); // charges summary dialog

  // ── TPIN dialog state ─────────────────────────────────────────────────────
  const [tpinOpen, setTpinOpen] = useState(false);
  const [tpin, setTpin] = useState("");
  const inputRefs = useRef([]);

  // ── Result dialogs ────────────────────────────────────────────────────────
  const [successOpen, setSuccessOpen] = useState(false);
  const [insufficientOpen, setInsufficientOpen] = useState(false);
  const [insufficientMsg, setInsufficientMsg] = useState("");

  const ip = useIPAddress();
  const bbpsUrls = useSelector((state) => state.auth.bbpsUrls);
  const CreditcardURL = bbpsUrls?.CreditCard;

  const mobile = JSON.parse(sessionStorage.getItem("mobileNumber"));
  const session = JSON.parse(sessionStorage.getItem("SessionID"));

  // ─── Fetch operator list ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchBoards = async () => {
      try {
        setLoading(true);
        const response = await api.post(`${baseapi}/getbbpsservices`, {
          url: CreditcardURL,
        });

        if (Array.isArray(response.data)) {
          setOperators(response.data);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBoards();
  }, []);

  const handleDynamicChange = (param, value) => {
    setDynamicValues((prev) => ({
      ...prev,
      [param]: value,
    }));
  };

  // ─── Build the Optional4 payload for the PAYMENT call ─────────────────────
  // This is NOT just the raw billerResponseInfo from the bill-fetch step —
  // it's a fresh XML document assembled from data already collected during
  // bill-fetch (Optional1/Optional2 param pairs, minimum/outstanding amounts,
  // and the raw bill details), then Base64-encoded. Structure:
  //   <inputParams><input><paramName/><paramValue/></input>...</inputParams>
  //   <additionalInfo><info><infoName/><infoValue/></info>...</additionalInfo>
  //   <billerResponse><billAmount/><billDate/><customerName/><dueDate/></billerResponse>
  const buildPaymentOptional4 = (details) => {
    const inputParamsXML = (details.Optional1 || [])
      .map((name, idx) => {
        const value = details.Optional2?.[idx] ?? "";
        return `<input><paramName>${name}</paramName><paramValue>${value}</paramValue></input>`;
      })
      .join("");

    const additionalInfoXML =
      `<info><infoName>Minimum Amount Due</infoName><infoValue>${details.minimumAmountDue || ""}</infoValue></info>` +
      `<info><infoName>Maximum Permissible Amount</infoName><infoValue>${details.maximumPermissibleAmount || ""}</infoValue></info>`;

    const billerResponseXML =
      `<billAmount>${details.billAmount || ""}</billAmount>` +
      `<billDate>${details.billDate || ""}</billDate>` +
      `<customerName>${details.customerName || ""}</customerName>` +
      `<dueDate>${details.dueDate || ""}</dueDate>`;

    const fullXML =
      `<inputParams>${inputParamsXML}</inputParams>` +
      `<additionalInfo>${additionalInfoXML}</additionalInfo>` +
      `<billerResponse>${billerResponseXML}</billerResponse>`;

    return btoa(fullXML);
  };

  // ─── Optional5 — always the same static empty <amountTags/> blob ──────────
  const buildPaymentOptional5 = () => btoa("<amountTags></amountTags>");

  // ─── Find the cardholder's mobile number from the dynamic bill-fetch
  // fields the user actually filled in. Different operators name this
  // field differently (e.g. "Registered Mobile Number", "Mobile Number"),
  // so this searches by key instead of hardcoding one exact paramName.
  // Reads from the Optional1/Optional2 snapshot already captured in
  // billDetails at bill-fetch time (not live dynamicValues state), since
  // that's the exact data that was actually sent to the bill-fetch call.
  // This is NOT the logged-in session mobile number — it's whatever
  // number the user typed into the bill-fetch form. ──────────────────────
  const getCardholderMobileFromBillDetails = (details) => {
    const names = details?.Optional1 || [];
    const values = details?.Optional2 || [];
    const idx = names.findIndex((name) =>
      (name || "").toLowerCase().includes("mobile"),
    );
    return idx !== -1 ? values[idx] : "";
  };

  const isTataPlay = selectedOperatorData
    ? selectedOperatorData.Text?.toLowerCase().includes("tata")
    : false;

  // ─── Build Optional1/Optional2 from whatever dynamic fields are filled ────
  const buildOptionalParams = () => {
    // CASE 1: BHAR00000NATB4 → multiple text fields, all of them included
    if (selectedOperatorData?.Value === "BHAR00000NATB4") {
      const filled = dynamicFields.filter(
        (f) =>
          dynamicValues[f.paramName] &&
          dynamicValues[f.paramName].trim() !== "",
      );

      return {
        Optional1: filled.map((f) => f.paramName),
        Optional2: filled.map((f) => dynamicValues[f.paramName]),
      };
    }

    // CASE 2: Tata Play → dropdown identifier + a single matching input
    if (isTataPlay) {
      if (!selectedField) {
        alert("Please select identifier");
        return null;
      }

      const val = dynamicValues[selectedField];
      if (!val || val.trim() === "") {
        alert(`Please enter value for ${selectedField}`);
        return null;
      }

      return {
        Optional1: [selectedField],
        Optional2: [val],
      };
    }

    // CASE 3: Every other operator → use all filled-in text fields
    const filled = dynamicFields.filter(
      (f) =>
        dynamicValues[f.paramName] && dynamicValues[f.paramName].trim() !== "",
    );

    return {
      Optional1: filled.map((f) => f.paramName),
      Optional2: filled.map((f) => dynamicValues[f.paramName]),
    };
  };

  // ─── Step 1 (BillFetchEnabled operators): fetch the bill ─────────────────
  const handleFetchBill = async () => {
    if (!operator) return alert("Select operator");

    const opt = buildOptionalParams();
    if (!opt) return; // stop if validation failed inside buildOptionalParams

    const { Optional1, Optional2 } = opt;
    const accountValue = Optional2.length ? Optional2[0] : "";

    const payload = {
      ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
      MachineID: ip,
      Mobilenumber: mobile,
      SessionID: session,
      Date: date,
      OrderId: orderID,
      TPIN: "",

      RequestType: "BA_BBPS_Billfetch",
      Account_num: accountValue,
      SpKey: operator,
      Amount: "1",

      Optional1,
      Optional2,
      Optional3: "true",
      Optional4: "",
      Optional5: "",
      Optional6: "",
      Optional7: "",
      Statement: "NOT_SUPPORTED",
      Narration: "",
      IsBillFetch: "",
      Platform: Platform,
      ApplicationType: ApplicationType,
      EntityID: entity_id,
      GeoLocation: locationGeo,
      ServiceID: "3",
      ParameterInfo: "",
    };

    const res = await api.post(
      `${baseapi}/JanAadhaareWalletBBPSPayments`,
      payload,
    );

    if (res.data.Response_Code === "1000") {
      const parsed = parseBillResponse(
        res.data.amountOptions,
        res.data.billerResponseInfo,
      );

      setBillDetails({
        ...parsed,
        orderId: res.data.OrderId,
        Optional1,
        Optional2,
        billAmount: res.data.billAmount, // raw paise value, e.g. "685200"
        billDate: parsed?.billDate || "",
        billerResponseInfo: res.data.billerResponseInfo,
      });

      // Pre-fill the editable "Enter Amount to Pay" field with the
      // minimum amount, but the user can change it — whatever value sits
      // in this field at submit time is what actually gets paid.
      setPayAmount("");
      setBillFetchOpen(true);
    } else {
      alert(res.data.Message);
    }
  };

  // ─── Step 2 (BillFetchEnabled operators): call GetCharges for the
  // user-entered amount, then SHOW A CHARGES SUMMARY DIALOG (not TPIN yet).
  // The user sees TransactionAmount / Charges / GST / TotalPayable and
  // clicks "Pay Now" to proceed to TPIN. ────────────────────────────────────
  const handlePayNowFromBill = async () => {
    if (!payAmount) {
      alert("Please enter amount");
      return;
    }

    if (Number(payAmount) < 100) {
      alert("Minimum amount should be ₹100");
      return;
    }

    setChargesLoading(true);

    try {
      const chargesPayload = {
        ReqType: "TA_BBPS_GetCharges",
        TrxnAmount: payAmount,
        BillerID: selectedOperatorData?.Value || operator,
        EntityID: entity_id,
        MachineID: ip,
        ApplicationType: ApplicationType,
        Platform: Platform,
        GeoLocation: locationGeo,
        SessionID: session,
        Mobilenumber: mobile,
        ServiceID: "3",
      };

      const chargesRes = await api.post(
        `${baseapi}/getbbpscharges`,
        chargesPayload,
      );

      const resData = chargesRes.data;

      if (resData?.Response_Code !== "1000" || !resData?.TotalPayable) {
        alert(
          resData?.Message || "Unable to calculate charges. Please try again.",
        );
        return;
      }

      const accountValue = billDetails.Optional2?.[0] || "";

      // Build payment payload now (TotalPayable as Amount), store it —
      // don't fire it yet. Show charges summary dialog first.
      const prepared = {
        ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
        MachineID: ip,
        Mobilenumber: mobile,
        SessionID: session,
        Date: date,
        OrderId: billDetails.orderId,
        TPIN: "",
        RequestType: "BA_BBPS_PAYMENT",
        Account_num: accountValue,
        CustomerID: accountValue,
        SpKey: operator,
        Amount: payAmount,
        Optional1: billDetails.Optional1,
        Optional2: billDetails.Optional2,
        Optional3: "true",
        Optional4: billDetails.billerResponseInfo,
        Optional5: buildPaymentOptional5(),
        Optional6: "NOT_SUPPORTED",
        Optional7: "",
        IsBillFetch: "",
        Statement: "ProcessDebit",
        Narration: `Payment of Rs. ${resData.TotalPayable} to ${billDetails.billerName || "Bill"}`,
        Platform: Platform,
        ApplicationType: ApplicationType,
        EntityID: entity_id,
        GeoLocation: location,
        ServiceID: "3",
        ParameterInfo: billDetails.billerName || "Bill Payment",
        IsExternalTransaction: "1",
        RemitterName: billDetails.customerName,
      };
      console.log(prepared);
      setPreparedPaymentPayload(prepared);
      setChargesData(resData); // store for the charges summary dialog
      setBillFetchOpen(false); // close bill details dialog
      setChargesOpen(true); // open charges summary dialog
    } catch (error) {
      console.error("Get charges error:", error);
      alert("Unable to calculate charges. Please try again.");
    } finally {
      setChargesLoading(false);
    }
  };

  // ─── Step 3: user clicks "Pay Now" in the charges summary dialog ──────────
  // Closes charges dialog, clears any old TPIN entry, opens TPIN dialog.
  const handleConfirmPayment = () => {
    setChargesOpen(false);
    setTpin("");
    setTpinOpen(true);
  };

  // ─── Quick Pay path (BillFetchEnabled === false operators): no bill
  // fetch step at all — the amount the user typed into the plain "Amount"
  // field goes straight into the payment payload. ───────────────────────────
  const handleQuickPay = () => {
    const opt = buildOptionalParams();
    if (!opt) return;

    const { Optional1, Optional2 } = opt;

    const prepared = {
      ReqType: "Jan_Aadhaar_eWallet_BBPSPayments",
      MachineID: ip,
      Mobilenumber: mobile,
      SessionID: session,
      Date: date,
      OrderId: orderID,
      TPIN: "",

      RequestType: "BA_NoNBBPS_Payment",
      Account_num: mobile,
      CustomerID: mobile,
      SpKey: operator,
      Amount: amount, // ✅ user-entered amount from the plain Amount field

      Optional1,
      Optional2,
      Optional3: "true",
      Optional4: "",
      Optional6: "NOT_SUPPORTED",

      Statement: "ProcessDebit",
      Narration: `Payment of Rs. ${amount}`,
      Platform: Platform,
      ApplicationType: ApplicationType,
      EntityID: entity_id,
      GeoLocation: location,
      ServiceID: "3",
      ParameterInfo: "Quick Pay",
      IsExternalTransaction: "1",
      RemitterName: billDetails.customerName,
    };

    setPreparedPaymentPayload(prepared);
    setTpinOpen(true);
  };

  // ─── TPIN submit → fires whichever payment (bill-pay or quick-pay) was
  // prepared above, merging in the TPIN the user just entered. ──────────────
  const handleSubmitTPIN = async () => {
    const finalPayload = {
      ...preparedPaymentPayload,
      TPIN: tpin,
    };

    try {
      const res = await api.post(
        `${baseapi}/JanAadhaareWalletBBPSPayments`,
        finalPayload,
      );

      if (res.data.Response_Code === "1000") {
        setTpinOpen(false);
        setSuccessOpen(true);
      } else if (
        res.data.Response_Code === "2001" ||
        res.data.Message?.includes("Insufficient")
      ) {
        setTpinOpen(false);
        setInsufficientMsg(res.data.Message || "Insufficient Wallet Balance");
        setInsufficientOpen(true);
      } else {
        alert(res.data.Message);
      }
    } catch (error) {
      console.error("Payment error:", error);
      alert("Server error during payment");
    }
  };

  // ─── XML helpers for parsing the BBPS bill-fetch response ─────────────────
  const decodeBase64XML = (base64) => {
    try {
      const xmlString = atob(base64);
      const wrappedXML = `<root>${xmlString}</root>`;
      const parser = new DOMParser();
      return parser.parseFromString(wrappedXML, "text/xml");
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  const parseBillResponse = (amountOptions, billerResponseInfo) => {
    const amountXML = decodeBase64XML(amountOptions);
    const billerXML = decodeBase64XML(billerResponseInfo);

    if (!amountXML || !billerXML) return null;

    const getValue = (xml, tag) =>
      xml.getElementsByTagName(tag)[0]?.textContent || "";

    const minimumAmount = (
      parseFloat(getValue(amountXML, "billAmount")) / 100
    ).toFixed(2);

    const customerName = getValue(amountXML, "customerName");
    const dueDate = getValue(amountXML, "dueDate");
    const billDate = getValue(amountXML, "billDate");

    const additionalInfo = Array.from(
      billerXML.getElementsByTagName("info")
    ).map((item) => ({
      infoName:
        item.getElementsByTagName("infoName")[0]?.textContent || "",
      infoValue:
        item.getElementsByTagName("infoValue")[0]?.textContent || "",
    }));

    return {
      customerName,
      billerName: selectedOperatorData?.Text || "",
      dueDate,
      billDate,
      minimumAmount,
      additionalInfo,
    };
  };

  return (
    <>
      <CssBaseline />
      <Box
        sx={{
          background: "#f8fafc",
          px: { xs: 2, md: 6 },
          transition: "background 0.4s ease",
        }}
      >
        <Grid container spacing={3}>
          {/* Left Card */}
          <Grid size={{ xs: 12, md: 4 }} item xs={12} md={4}>
            <Card
              elevation={isDark ? 0 : 2}
              sx={{
                background: "white",
                borderRadius: 3,
                p: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                border: "1px solid #f0f0f0",
              }}
            >
              <CardContent>
                <Typography
                  variant="h5"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: 15 },
                  }}
                >
                  CreditCard
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: { md: 2 }, mt: { md: 2 } }}
                >
                  Select your creditcard service provider
                </Typography>

                <FormControl fullWidth variant="filled" sx={{ mt: 1, mb: 2 }}>
                  <Autocomplete
                    options={operators}
                    getOptionLabel={(option) => option.Text}
                    value={operators.find((o) => o.Value === operator) || null}
                    onChange={(event, newValue) => {
                      if (!newValue) {
                        setOperator("");
                        setSelectedOperatorData(null);
                        setDynamicFields([]);
                        setDynamicValues({});
                        return;
                      }

                      setOperator(newValue.Value);

                      setSelectedOperatorData(newValue);

                      setSelectedField("");

                      if (newValue.BBPSData) {
                        const fields = JSON.parse(newValue.BBPSData);
                        setDynamicFields(fields);

                        const initial = {};
                        fields.forEach((f) => {
                          initial[f.paramName] = "";
                        });

                        setDynamicValues(initial);
                      } else {
                        setDynamicFields([]);
                        setDynamicValues({});
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select your bank"
                        placeholder="Search bank..."
                        variant="filled"
                      />
                    )}
                    sx={{
                      "& .MuiFilledInput-root": {
                        backgroundColor: "#f8fafc",
                        borderRadius: 1,
                        border: isDark ? "#12303a" : "1px solid #999",
                      },
                    }}
                  />
                </FormControl>

                {/* Conditional Rendering */}
                {operator && selectedOperatorData && (
                  <>
                    {/* 🟣 CASE 1: TATA PLAY → DROPDOWN + SINGLE FIELD */}
                    {isTataPlay ? (
                      <>
                        {/* Dropdown label */}
                        <Typography variant="caption" color="text.secondary">
                          Select Identifier
                        </Typography>

                        <FormControl
                          fullWidth
                          variant="filled"
                          sx={{ mt: 1, mb: 2 }}
                        >
                          <InputLabel>
                            Mobile Number / Subscriber Number
                          </InputLabel>
                          <Select
                            value={selectedField}
                            onChange={(e) => setSelectedField(e.target.value)}
                          >
                            {dynamicFields.map((field, index) => (
                              <MenuItem key={index} value={field.paramName}>
                                {field.paramName}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* Only one TextField based on selected dropdown option */}
                        {selectedField && (
                          <>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {selectedField}
                            </Typography>
                            <TextField
                              fullWidth
                              placeholder={selectedField}
                              autoComplete="off"
                              variant="filled"
                              value={dynamicValues[selectedField] || ""}
                              onChange={(e) =>
                                handleDynamicChange(
                                  selectedField,
                                  e.target.value,
                                )
                              }
                              sx={{
                                mt: 1,
                                mb: 2,
                                backgroundColor: "#f8fafc",
                                borderRadius: 1,
                                "& .MuiFilledInput-root": {
                                  backgroundColor: isDark
                                    ? "#12303a"
                                    : "#f0f0f0",
                                  border: isDark ? "#12303a" : "1px solid #999",
                                  borderRadius: 1,
                                },
                              }}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {/* 🟢 CASE 2: ALL OTHER OPERATORS → NORMAL TYPOGRAPHY + TEXTFIELDS */}
                        {dynamicFields.map((field, index) => (
                          <React.Fragment key={index}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {field.paramName}
                            </Typography>

                            <TextField
                              fullWidth
                              autoComplete="off"
                              placeholder={field.paramName}
                              variant="filled"
                              value={dynamicValues[field.paramName] || ""}
                              onChange={(e) =>
                                handleDynamicChange(
                                  field.paramName,
                                  e.target.value,
                                )
                              }
                              sx={{
                                mt: 1,
                                mb: 2,
                                backgroundColor: "#f8fafc",
                                borderRadius: 1,
                                "& .MuiFilledInput-root": {
                                  backgroundColor: isDark
                                    ? "#12303a"
                                    : "#f0f0f0",
                                  border: isDark ? "#12303a" : "1px solid #999",
                                  borderRadius: 1,
                                },
                              }}
                            />
                          </React.Fragment>
                        ))}
                      </>
                    )}

                    {/* Amount only when BillFetchEnabled === false */}
                    {selectedOperatorData.BillFetchEnabled === false && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Amount
                        </Typography>

                        <TextField
                          fullWidth
                          placeholder="Enter amount"
                          variant="filled"
                          autoComplete="off"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          sx={{
                            mt: 1,
                            mb: 3,
                            backgroundColor: "#f8fafc",
                            borderRadius: 1,
                            "& .MuiFilledInput-root": {
                              backgroundColor: "#f8fafc",
                              border: isDark ? "#12303a" : "1px solid #999",
                              borderRadius: 1,
                            },
                          }}
                        />
                      </>
                    )}

                    {/* Proceed Button */}
                    <Button
                      fullWidth
                      size="large"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => {
                        if (selectedOperatorData?.BillFetchEnabled) {
                          handleFetchBill();
                        } else {
                          handleQuickPay();
                        }
                      }}
                      sx={{
                        background: "linear-gradient(135deg, #16a34a, #22c55e)",
                        color: "#fff",
                        borderRadius: 2,
                        py: 1.2,
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                    >
                      {selectedOperatorData.BillFetchEnabled
                        ? "Fetch Bill"
                        : "Quick Pay"}
                    </Button>
                  </>
                )}

                {/* Secure Info */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 3,
                    color: "text.secondary",
                  }}
                >
                  <ShieldOutlinedIcon
                    sx={{ color: theme.palette.primary.main }}
                  />
                  <Typography variant="body2">
                    100% Secure & Instant Recharge
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Card */}
          {!isMobile && (
            <>
              <Grid size={{ xs: 12, md: 8 }} item xs={12} md={8}>
                <Card
                  elevation={isDark ? 0 : 2}
                  sx={{
                    background: "white",
                    borderRadius: 3,
                    p: 2,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
                    height: "100%",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          background: isDark
                            ? "rgba(35,190,165,0.12)"
                            : "rgba(0,123,255,0.12)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: theme.palette.primary.main,
                          mr: 1,
                        }}
                      >
                        <SearchIcon />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ color: "text.primary", fontWeight: 700 }}
                      >
                        Browse Plans
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        background: isDark ? "#12303a" : "rgba(0,0,0,0.05)",
                        borderRadius: 2,
                        p: 3,
                        color: "text.secondary",
                        mt: 1,
                        mb: 2,
                        minHeight: 92,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography>No plans available.</Typography>
                    </Box>

                    <Box
                      sx={{
                        background: isDark ? "#0f3a35" : "rgba(0,123,255,0.05)",
                        borderRadius: 1.5,
                        p: 1.2,
                        color: "text.secondary",
                        mt: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        }}
                      >
                        Disclaimer:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        While we support most recharges, please verify with your
                        operator before proceeding.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid size={{ xs: 12 }} item xs={12}>
                <Box
                  sx={{
                    borderRadius: md ? 2 : 0,
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    Trusted by <b>UIDAI BBPS Network</b>
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Bill Details Dialog */}
      <Dialog
        open={billFetchOpen}
        onClose={() => setBillFetchOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>
          Bill Details
        </DialogTitle>

        <DialogContent dividers>
          {billDetails ? (
            <Box sx={{ py: 1 }}>
              <Typography sx={{ mb: 2 }}>
                <b>Customer Name :</b> {billDetails.customerName}
              </Typography>

              <Typography sx={{ mb: 2 }}>
                <b>Biller Name :</b> {billDetails.billerName}
              </Typography>

              <Typography sx={{ mb: 2 }}>
                <b>Due Date :</b> {billDetails.dueDate}
              </Typography>

              <Typography sx={{ mb: 2 }}>
                <b>Total Amount Due :</b> ₹{billDetails.minimumAmount}
              </Typography>

              {billDetails.additionalInfo?.map((item, index) => (
                <Typography key={index} sx={{ mb: 2 }}>
                  <b>{item.infoName} :</b>{" "}
                  {isNaN(item.infoValue) ? item.infoValue : `₹${item.infoValue}`}
                </Typography>
              ))}

              {/* ── This is the ONLY amount that gets sent for payment.
                  Everything above (minimum / due / outstanding) is shown
                  purely for the user's reference. ── */}
              <Typography fontWeight={600} mb={1} color="text.secondary">
                Enter Amount to Pay
              </Typography>

              <TextField
                fullWidth
                placeholder="Enter amount"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                type="number"
                inputProps={{
                  min: 100,
                }}
                InputProps={{
                  startAdornment: (
                    <Typography
                      sx={{
                        mr: 1,
                        fontWeight: 700,
                        color: "primary.main",
                      }}
                    >
                      ₹
                    </Typography>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    background: "#fff",
                  },
                }}
              />
            </Box>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handlePayNowFromBill}
            disabled={chargesLoading}
          >
            {chargesLoading ? "Calculating charges..." : "Quick Pay"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Charges Summary Dialog */}
      <Dialog
        open={chargesOpen}
        onClose={() => setChargesOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center" }}>
          Payment Summary
        </DialogTitle>

        <DialogContent dividers>
          {chargesData && (
            <Box sx={{ py: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                  pb: 1.5,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography color="text.secondary">
                  Transaction Amount
                </Typography>
                <Typography fontWeight={600}>
                  ₹{chargesData.TransactionAmount}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography color="text.secondary">Charges</Typography>
                <Typography fontWeight={600}>₹{chargesData.Charges}</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                  pb: 1.5,
                  borderBottom: "1px dashed #e0e0e0",
                }}
              >
                <Typography color="text.secondary">GST</Typography>
                <Typography fontWeight={600}>₹{chargesData.GST}</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 1,
                }}
              >
                <Typography fontWeight={700} fontSize={16}>
                  Total Payable
                </Typography>
                <Typography
                  fontWeight={700}
                  fontSize={16}
                  color="primary.main"
                  sx={{ color: "#16a34a" }}
                >
                  ₹{chargesData.TotalPayable}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setChargesOpen(false)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleConfirmPayment}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              background: "linear-gradient(135deg, #16a34a, #22c55e)",
              color: "#fff",
              fontWeight: 700,
            }}
          >
            Pay Now ₹{chargesData?.TotalPayable}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TPIN Dialog */}
      <Dialog
        open={tpinOpen}
        onClose={() => setTpinOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700 }}>
          Enter TPIN
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
            {[0, 1, 2, 3].map((index) => (
              <TextField
                key={index}
                type="password"
                value={tpin[index] || ""}
                inputRef={(el) => (inputRefs.current[index] = el)}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(-1);

                  const newTpin = tpin.padEnd(4, "").split("");
                  newTpin[index] = value;

                  setTpin(newTpin.join(""));

                  // Move to next box automatically
                  if (value && index < 3) {
                    inputRefs.current[index + 1]?.focus();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !tpin[index] && index > 0) {
                    inputRefs.current[index - 1]?.focus();
                  }
                }}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "22px",
                    fontWeight: 600,
                  },
                }}
                sx={{
                  width: 60,
                  "& .MuiOutlinedInput-root": {
                    height: 60,
                    borderRadius: 3,
                  },
                }}
              />
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmitTPIN}
            disabled={tpin.length !== 4}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog open={successOpen} onClose={() => setSuccessOpen(false)}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          Payment Successful🎉
        </DialogTitle>
      </Dialog>

      {/* Insufficient Balance Dialog */}
      <Dialog
        open={insufficientOpen}
        onClose={() => setInsufficientOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            width: 380,
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
            fontSize: 18,
            pb: 0.5,
          }}
        >
          ⚠️ Insufficient Balance
        </DialogTitle>

        <DialogContent sx={{ textAlign: "center", mt: 1 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {insufficientMsg}
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setInsufficientOpen(false)}
            fullWidth
            variant="outlined"
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>

          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setInsufficientOpen(false);
              navigate("/dashboard/loadmoney");
            }}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              ml: 1,
              color: "white",
            }}
          >
            Load Money
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 },
        }}
      >
        <DialogContent sx={{ textAlign: "center", pt: 4, pb: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "rgba(22,163,74,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
              fontSize: 36,
            }}
          >
            ✅
          </Box>

          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Payment Successful
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Your payment has been processed successfully.
          </Typography>

          <Box
            sx={{
              background: "#f8fafc",
              borderRadius: 2,
              p: 2,
              textAlign: "left",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography color="text.secondary" fontSize={14}>
                Amount Paid
              </Typography>
              <Typography fontWeight={600} fontSize={14}>
                ₹{preparedPaymentPayload?.Amount}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography color="text.secondary" fontSize={14}>
                Biller
              </Typography>
              <Typography fontWeight={600} fontSize={14}>
                {billDetails?.billerName || selectedOperatorData?.Text || "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography color="text.secondary" fontSize={14}>
                Order ID
              </Typography>
              <Typography fontWeight={600} fontSize={14}>
                {preparedPaymentPayload?.OrderId}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setSuccessOpen(false)}
            sx={{
              background: "linear-gradient(135deg, #16a34a, #22c55e)",
              color: "#fff",
              borderRadius: 2,
              py: 1.2,
              fontWeight: 700,
              textTransform: "none",
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
